import React, { useCallback, useMemo } from 'react';
import { ButtonGroup } from '@antimatterhq/ui/components/buttonGroup';
import { Input } from '@antimatterhq/ui/components/input';
import {
  convertTo12Hour,
  convertTo24Hour,
} from '@antimatterhq/ui/utils/dateUtils';
import { ZonedDateTime, toTimeZone } from '@internationalized/date';

export interface TimeInputProps {
  label?: string;
  value: ZonedDateTime;
  timezone: string;
  onChange?: (date: ZonedDateTime) => void;
  /**
   * Determines what second value should be used when the composed date is
   * returned.
   *  - "start" → 0 seconds (start of the minute – default)
   *  - "end"   → 59 seconds (end of the minute – helpful for inclusive
   *              range end-times)
   */
  secondBehaviour?: 'start' | 'end';
}

const periodOptions = [
  { label: 'AM', value: 'AM' },
  { label: 'PM', value: 'PM' },
];

export const TimeInput = React.memo(
  ({
    label,
    value,
    timezone,
    onChange,
    secondBehaviour = 'start',
  }: TimeInputProps) => {
    // Extract current time state from the ZonedDateTime value
    const timeState = useMemo(() => {
      // Make sure we're working in the correct timezone
      const zonedValue = toTimeZone(value, timezone);

      const hours = convertTo12Hour(zonedValue.hour);
      return {
        hour: String(hours.hour).padStart(2, '0'),
        minute: String(zonedValue.minute).padStart(2, '0'),
        period: hours.period,
      };
    }, [value, timezone]);

    const updateTime = useCallback(
      (field: 'hour' | 'minute' | 'period', fieldValue: string) => {
        const updatedTimeState = { ...timeState, [field]: fieldValue };

        const hour24 = convertTo24Hour(
          parseInt(updatedTimeState.hour),
          updatedTimeState.period,
        );

        // Create new ZonedDateTime with updated time
        const zonedValue = toTimeZone(value, timezone);
        const newZonedDateTime = zonedValue.set({
          hour: hour24,
          minute: parseInt(updatedTimeState.minute),
          second: secondBehaviour === 'end' ? 59 : 0,
        });

        onChange?.(newZonedDateTime);
      },
      [timeState, timezone, value, onChange, secondBehaviour],
    );

    const handleHourChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        const numValue = parseInt(inputValue);

        // Validate hour range (1-12)
        if (inputValue === '' || (numValue >= 1 && numValue <= 12)) {
          // Only pad when value is not empty
          updateTime(
            'hour',
            inputValue === '' ? '12' : inputValue.padStart(2, '0'),
          );
        }
      },
      [updateTime],
    );

    const handleMinuteChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        const numValue = parseInt(inputValue);

        // Validate minute range (0-59)
        if (inputValue === '' || (numValue >= 0 && numValue <= 59)) {
          // Only pad when value is not empty
          updateTime(
            'minute',
            inputValue === '' ? '00' : inputValue.padStart(2, '0'),
          );
        }
      },
      [updateTime],
    );

    const handlePeriodChange = useCallback(
      (selectedValue: string | undefined) => {
        updateTime('period', selectedValue || 'AM');
      },
      [updateTime],
    );

    return (
      <div className="flex h-8 items-center gap-2">
        {label && (
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {label}
          </span>
        )}
        <Input
          type="text"
          value={timeState.hour}
          onChange={handleHourChange}
          className="h-8 w-20 text-center"
          placeholder="12"
        />
        <Input
          type="text"
          value={timeState.minute}
          onChange={handleMinuteChange}
          className="h-8 w-20 text-center"
          placeholder="00"
        />
        <ButtonGroup
          options={periodOptions}
          value={timeState.period}
          onChange={handlePeriodChange}
          buttonClassName="min-w-0"
        />
      </div>
    );
  },
);

TimeInput.displayName = 'TimeSelector';
